import { DropdownMenuOptions } from "../../mp-shared-components/generic-shared-components/components"
import { ButtonProps } from "../../mp-shared-components/generic-shared-components/components/Button"
import { ChipColor } from "../../mp-shared-components/generic-shared-components/components/Chip"
import { Role } from "../../mp-database"
import { FeatureFlagName } from "../../mp-utils/generic-utils/feature-flags/types"
import { FiltersState } from "../states/filters"
import { LicenseNavCategories } from "../../../pages/Router"

export * from "./savedReports"
export * from "./api"
export * from "./goals"

export type CustomViewType = {
  id: string
  pageId?: string
  name: string
  filters: Record<string, string[]>
  isDefault: boolean
}

export type NavItem = {
  key?: string
  label?: string
  href?: string
  icon?: string
  subtitle?: string
  onClick?: (event: React.MouseEvent) => void
  children?: NavItem[]
  isRoot?: boolean
  isEntity?: boolean
  loading?: boolean
  selected?: boolean
  featureFlag?: FeatureFlagName
  group?: "Insights" | "Data" | "Configuration"
  hideSideNav?: boolean
  hideBreadcrumb?: boolean
  dismissable?: boolean
  navCategory?: LicenseNavCategories[]
  beta?: boolean
}

export type FilterOptions = {
  singleLevel?: boolean
  setQuestionsAvailablePrograms?: boolean
}

export type FilterRouteConfig = {
  filters: string[]
  filterOptions?: Record<string, FilterOptions>
}

export type FilterTypeItem = {
  key: string
  label: string
  icon: string
  wide?: boolean
  type?:
    | "date"
    | "boolean"
    | "select"
    | "program"
    | "score-range"
    | "usage-content-type"
    | "skill"
}

export type FilterTypeOptions = {
  filter: string
  options: { label: string; value: string }[]
}

export type UserFilterItem = {
  filter: string
  value: string[]
}

export type RouteParams = {
  page: string
  module: string
  submodule?: string
  subpage?: string
  entityType?: string
  entityId?: string
}

export type CubeRequestFilter = {
  member: string
  operator: string
  values: string[]
}

export type CubeRequestTimeDimension = {
  dimension: string
  dateRange: string
  granularity?: string
}

export type CubeRequestParams = {
  dimensions?: string[]
  measures?: string[]
  filters?: CubeRequestFilter[]
  timeDimensions?: CubeRequestTimeDimension[]
  order?: Record<string, "asc" | "desc">
  limit?: number
}

export type WidgetName =
  | "ActiveStates"
  | "ActiveOrganizations"
  | "ActiveOrganizationAdmins"
  | "ActiveDistrictAdmins"
  | "ActiveSchoolAdmins"
  | "ActiveStudents"
  | "ActiveTeachers"
  | "ActiveClasses"
  | "ActiveSchools"
  | "ActiveDistricts"
  | "AverageStudentTimeSpent"
  | "AverageDaysActive"
  | "ContentTypePracticeTests"
  | "ContentTypeMasteryLessons"
  | "ContentTypeBellRingers"
  | "ContentTypeSkillReview"
  | "ContentTypeMasteryCurriculum"
  | "ContentTypeEbooks"
  // Average Score/Accuracy
  | "ContentTypePracticeTestsAverageScore"
  | "ContentTypeMasteryCurriculumAverageAccuracy"
  | "ContentTypeMasteryLessonsAverageAccuracy"
  | "ContentTypeSkillReviewAverageAccuracy"
  | "PerformancePracticeTestsAverageScore"
  // ----
  // Average Time Spent
  | "ContentTypeBellRingersAverageTimeSpent"
  | "ContentTypePracticeTestAverageTimeSpent"
  | "ContentTypeMasteryCurriculumAverageTimeSpent"
  | "ContentTypeMasteryLessonsAverageTimeSpent"
  | "ContentTypeSkillReviewAverageTimeSpent"
  // ----
  // Entity Average Time Spent
  | "ContentTypeEntityBellRingersAverageTimeSpent"
  | "ContentTypeEntityPracticeTestAverageTimeSpent"
  | "ContentTypeEntityMasteryCurriculumAverageTimeSpent"
  | "ContentTypeEntityMasteryLessonsAverageTimeSpent"
  | "ContentTypeEntitySkillReviewAverageTimeSpent"
  // ----
  // Completed
  | "ContentTypeBellRingersCompletedPercent"
  | "ContentTypePracticeTestsCompletedPercent"
  | "ContentTypeMasteryCurriculumCompletedPercent"
  | "ContentTypeMasteryLessonsCompletedPercent"
  | "ContentTypeSkillReviewCompletedPercent"
  // ----
  // Assigned
  | "ContentPracticeTestAssigned"
  | "ContentBellRingersAssigned"
  | "ContentMasteryCurriculumAssigned"
  | "ContentMasteryLessonsAssigned"
  | "ContentSkillReviewAssigned"
  // ----
  | "AverageTestsCompleted"
  | "AverageQuestionsAnswered"
  | "AverageParticipants"

export type WidgetDataParams = {
  type:
    | "org"
    | "ats"
    | "ada"
    | "contentType"
    | "contentAverageScore"
    | "contentAverageAccuracy"
    | "contentAverageTimeSpent"
    | "contentCompletedPercent"
    | "contentAssigned"
    | "atc"
    | "aqa"
    | "ap"
    | "licenseAssignment"
    | "licenseStudentCount"
    | "bulkActionFilter"
  label?: string
  entity: string
  path?: string
  allowedFilters?: string[]
  allowedFiltersByRole?: Record<string, string[]>
  dynamicFilters?: string[]
  excludedRoles?: Role[]
  requests?: CubeRequestParams[]
  fields?: Record<string, string>
  contentType?: ContentType
  getDynamicFilters?: (
    requestIndex: number,
    filters: FiltersState
  ) => CubeRequestParams[]
  displayConfig?: {
    entity?: string
    tooltip?: string
    footerLabel?: string
  }
}

export type PeriodValue =
  | "this school year"
  | "entire school year"
  | "last 7 days"
  | "last 30 days"
  | "last school year"
  | "custom"

export type RangeOption = {
  value: PeriodValue
  label: string
  chipLabel?: string
}

export enum EventActivityCode {
  bellRinger = "bell-ringer",
  masteryCurriculum = "mastery-curriculum",
  masteryLesson = "mastery-lesson",
  practiceTest = "practice_test",
  skillReview = "skill-review"
}

export enum EventTypeCode {
  questionAnswered = "question_answered",
  lessonStart = "lesson_start",
  lessonResume = "lesson_resume",
  lessonComplete = "lesson_complete",
  pageView = "page_view",
  login = "login",
  logout = "logout",
  testStart = "test_start",
  testResume = "test_resume",
  testSubmit = "test_submit"
}

/**
 * Possible actions for a data table.
 * Updated order for accessibility so tab focus matches DOM order of buttons in TableActions component
 */
export enum TableActionsEnum {
  restore = "restore",
  import = "import",
  download = "download",
  new = "new",
  viewItem = "viewItem"
}

/**
 * Possible table grouping.
 */
export enum TableGroupedActionsEnum {
  groupedToggle = "groupedToggle",
  groupedSelector = "groupedSelector"
}

/**
 * Possible bulk actions for selected table rows.
 */
export enum TableBulkAction {
  delete = "delete",
  resetPassword = "resetPassword",
  resendInvite = "resendInvite",
  addToPlan = "addToPlan",
  assignLicense = "assignLicense",
  unassignLicense = "unassignLicense"
}

/**
 * State for select all functionality
 */
export interface SelectAllState {
  isSelectAllActive: boolean
  totalCount: number
  entityName: string
}

/**
 * Bulk action payload that supports both ID-based and filter-based operations
 */
export interface BulkActionPayload {
  selectedIds?: string[]
  filters?: FiltersState
  useFilters: boolean
  totalCount?: number
}

/**
 * Possible actions for a table row.
 */
export enum TableRowAction {
  view = "view",
  edit = "edit",
  delete = "delete",
  impersonate = "impersonate",
  assignStudents = "assignStudents",
  resendInvite = "resendInvite",
  viewContent = "viewContent",
  assignContent = "assignContent",
  viewItem = "viewItem",
  viewActivityLog = "viewActivityLog",
  testReview = "testReview",
  viewAnswersReport = "viewAnswersReport",
  viewTestReport = "viewTestReport",
  preview = "preview",
  questionPreview = "questionPreview",
  addToPlan = "addToPlan",
  start = "start",
  assignLicense = "assignLicense",
  unassignLicense = "unassignLicense",
  endAssignment = "endAssignment",
  assignLicensePool = "assignLicensePool",
  downloadResource = "downloadResource",
  studentReport = "studentReport"
}

export type ActionOption = Omit<DropdownMenuOptions, "icon"> & {
  icon?: string
  buttonProps?: Partial<ButtonProps>
  loading?: boolean
  entityLabel?: string
  tooltip?: string
}

export type RowActionOption = Omit<ActionOption, "action"> & {
  action?: (id: string, details?: any) => void
  getHref?: (id: string, details?: any) => string
  getTooltip?: (record: { [key: string]: string }) => string
}

export type TableOptions = {
  searchPlaceholder?: string
  pageName?: string
  selectable?: boolean
  entityLabel?: string
  hideSearch?: boolean
  hideColumnSelector?: boolean
  deleteMessageTemplate?: (record: any) => string
  bulkDeleteMessageTemplate?: (records: any[]) => string
  supportsSelectAll?: boolean
  entityName?: string
  isExpandable?: boolean
  caption?: string
}

export type CountName =
  | "Districts"
  | "Schools"
  | "ActiveStudents"
  | "Teachers"
  | "DistrictAdmins"
  | "Students"
  | "SchoolAdmins"
  | "Classes"
  | "ApproxDuration"
  | "InteractionsCount"
  | "QuestionsCount"
  | "PracticeQuestionsCount"

export type DetailsDataFormat =
  | "date"
  | "boolean"
  | "duration"
  | "array"
  | "email"
  | "dateTime"
  | "duration"
  | "grade"
  | "reportingCategory"

export type CountDataParams = {
  label: string
  icon: string
  format?: DetailsDataFormat
}

export type TableItem = {
  label: string
  entity: string
  entityLabel?: string
  icon?: string
  page?: string
  requestParam?: string
  featureFlag?: FeatureFlagName
}

export type DetailsExtraItem = {
  label: string
  value: string
  format?: DetailsDataFormat
}

export type ChipItem = {
  value: string
  color: ChipColor
}

export type DetailsSubtitleData = {
  subtitle?: string | string[]
  subtitleLink?: string
  subtitleEntity?: string
}

export type DetailsDataParams = {
  id?: string
  title: string
  description?: string
  subtitlesData?: DetailsSubtitleData[]
  entity: string
  isActive?: string
  chips?: ChipItem[]
  editable: boolean
  onEdit?: () => void
  impersonatable?: boolean
  onImpersonate?: () => void
  actions?: TableRowAction[]
  details?: DetailsExtraItem[]
  detailsColumns?: number
  counts?: Record<string, string>
  tables?: TableItem[]
}

export enum ContentType {
  bellRinger = "element",
  practiceTest = "practice_test",
  quiz = "quiz",
  masteryLesson = "mastery_lesson",
  masteryCurriculum = "mastery_curriculum",
  skillReview = "study_hall",
  digitalResource = "downloadable_resource"
}

export enum UserRole {
  teacher = "Teacher",
  student = "Student",
  districtAdmin = "District Admin",
  schoolAdmin = "School Admin",
  superAdmin = "Super Administrator",
  actAdmin = "ACT Admin",
  actMultiDistrictAdmin = "Organization Admin"
}

export enum EntityModalType {
  add = "add",
  assignStudents = "assignStudents",
  edit = "edit",
  delete = "delete",
  impersonate = "impersonate",
  bulkDelete = "bulkDelete",
  import = "import",
  restore = "restore",
  bulkAssignLicense = "bulkAssignLicense",
  bulkUnassignLicense = "bulkUnassignLicense",
  assignLicensePool = "assignLicensePool"
}

export enum SubtestEnum {
  reading = "reading",
  science = "science",
  math = "math",
  english = "english",
  history = "history",
  overall = "overall",
  biology = "biology",
  writing = "writing"
}

export type SubtestContent = {
  label: string
  id: string | SubtestEnum
  path?: string
  program?: string
  filterValue?: string
  icon?: string
}

export type AvailablePrograms = {
  program: string
  displayName: string
  courses: SubtestContent[]
}

export type GetStartedArticleData = {
  textBlocks: string[]
  videoUrl?: string
  action?: {
    label: string
    url: string
  }
}
